import { cn } from '@/utils/cn'
import { Achievement } from '@/types/achievement'

interface AchievementBadgeProps {
  achievement: Achievement
  unlocked: boolean
  progress?: number
  size?: 'sm' | 'md' | 'lg'
  showProgress?: boolean
  className?: string
}

export function AchievementBadge({ 
  achievement, 
  unlocked, 
  progress = 0,
  size = 'md',
  showProgress = true,
  className 
}: AchievementBadgeProps) {
  const sizeClasses = {
    sm: {
      container: 'w-16 h-16',
      icon: 'text-2xl',
      progress: 'h-1'
    },
    md: {
      container: 'w-20 h-20',
      icon: 'text-3xl',
      progress: 'h-1.5'
    },
    lg: {
      container: 'w-24 h-24',
      icon: 'text-4xl',
      progress: 'h-2'
    }
  }
  
  const classes = sizeClasses[size]
  
  return (
    <div className={cn('relative group', className)}>
      <div 
        className={cn(
          'relative rounded-full flex items-center justify-center transition-all duration-300',
          classes.container,
          unlocked 
            ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg scale-100' 
            : 'bg-gray-200 dark:bg-gray-700 scale-95 grayscale'
        )}
      >
        <span className={cn(classes.icon, unlocked ? 'animate-bounce-slow' : 'opacity-50')}>
          {achievement.icon}
        </span>
        
        {!unlocked && showProgress && progress > 0 && (
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <svg className="w-full h-full -rotate-90">
              <circle
                cx="50%"
                cy="50%"
                r="45%"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-gray-300 dark:text-gray-600"
              />
              <circle
                cx="50%"
                cy="50%"
                r="45%"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeDasharray={`${progress * 2.83} 283`}
                className="text-primary transition-all duration-500"
              />
            </svg>
          </div>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
        <div className="bg-text text-bg text-sm rounded-full px-3 py-2 whitespace-nowrap">
          <p className="font-semibold">{achievement.name}</p>
          <p className="text-xs opacity-80">{achievement.description}</p>
          {!unlocked && showProgress && (
            <p className="text-xs opacity-60 mt-1">{Math.round(progress)}% complete</p>
          )}
        </div>
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
          <div className="border-4 border-transparent border-t-text"></div>
        </div>
      </div>
    </div>
  )
}

export function AchievementGrid({ 
  achievements, 
  userStats,
  className 
}: { 
  achievements: Achievement[]
  userStats: any
  className?: string 
}) {
  return (
    <div className={cn('grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4', className)}>
      {achievements.map(achievement => {
        const progress = getAchievementProgress(achievement, userStats)
        const unlocked = progress >= 100
        
        return (
          <AchievementBadge
            key={achievement.id}
            achievement={achievement}
            unlocked={unlocked}
            progress={progress}
            size="sm"
          />
        )
      })}
    </div>
  )
}

// Helper function (import from achievement types in real implementation)
function getAchievementProgress(achievement: Achievement, userStats: any): number {
  switch (achievement.requirement.type) {
    case 'streak':
      return Math.min((userStats.currentStreak / achievement.requirement.value) * 100, 100)
    case 'total_checkins':
      return Math.min((userStats.totalCheckIns / achievement.requirement.value) * 100, 100)
    case 'challenges_completed':
      return Math.min((userStats.completedChallenges / achievement.requirement.value) * 100, 100)
    case 'friends_invited':
      return Math.min((userStats.friendsInvited / achievement.requirement.value) * 100, 100)
    default:
      return 0
  }
}